import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import {
  clearSession,
  getStoredToken,
  getStoredUser,
  type MerchantUser,
} from "@/api/session";
import * as authApi from "@/api/client";

type AuthContextValue = {
  user: MerchantUser | null;
  isAuthenticated: boolean;
  login: typeof authApi.login;
  logout: () => Promise<void>;
  setUser: (user: MerchantUser | null) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MerchantUser | null>(() =>
    getStoredToken() ? getStoredUser() : null
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user && getStoredToken()),
      login: async (payload) => {
        const result = await authApi.login(payload);
        setUser(result.user);
        return result;
      },
      logout: async () => {
        await authApi.logout();
        setUser(null);
      },
      setUser: (next) => {
        if (!next) clearSession();
        setUser(next);
      },
    }),
    [user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
